import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import BetaLaunchLink from './BetaLaunchLink';

/**
 * Final CTA — same real "No More WhatsApp Agreements" copy and the same
 * BetaLaunchLink "cta" button as CallToAction.tsx (the live homepage's
 * version), but rendered as a normal, non-scroll-jacked section: the green
 * block is just there, in normal document flow, and only the text content
 * animates in via a standard whileInView fade/slide (same pattern every
 * other section on this page uses), not a pinned 300vh GSAP sequence.
 * CallToAction.tsx itself is untouched — it's still used by the live
 * LandingPage.tsx, so this is a separate component rather than a rewrite
 * of a component two pages depend on.
 */
export default function FinalCtaRevamp() {
  // The ball tracks scroll position directly, scrubbed against the
  // graphic's own natural height (no extra scroll spacer). With the
  // section's bottom padding now removed, there's less runway after the
  // graphic before the section ends, so the ball's full path + fade-out is
  // compressed further — into the first 40% of scrollYProgress instead of
  // 55% — so it's done and gone well before the graphic (and the padding-
  // free section around it) finishes scrolling through. useTransform clamps
  // outputs outside the given input range to the nearest breakpoint's
  // value, so opacity stays pinned at exactly 0 for the remaining 60% of
  // the scroll and everything after — the ball is fully, permanently gone
  // by the time the section has completely scrolled past, not just faded.
  const wormholeRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: wormholeRef, offset: ['start end', 'end start'] });
  const ballOffsetDistance = useTransform(scrollYProgress, [0, 0.4], ['0%', '100%']);
  const ballOpacity = useTransform(scrollYProgress, [0, 0.05, 0.35, 0.4], [0, 1, 1, 0]);

  return (
    <section className="relative bg-black pt-16 md:pt-24">
      {/* w-[98vw], not max-w-5xl — full width, matching the hero frame's own
          near-edge-to-edge treatment elsewhere on this page. */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-[98vw] mx-auto rounded-[2rem] md:rounded-[2.5rem] bg-accent px-6 py-16 md:py-24 flex flex-col items-center text-center overflow-hidden"
      >
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-[#1C1C1C] text-4xl sm:text-5xl md:text-[4.5rem] font-black tracking-tight leading-[1.1] mb-6 sm:mb-8"
        >
          No More <br />
          WhatsApp Agreements
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-[#1C1C1C] text-base sm:text-lg md:text-xl font-medium max-w-3xl leading-relaxed mb-8 sm:mb-10 px-2 sm:px-0"
        >
          Join freelancers from Bengaluru who are done working on blind trust. <br />
          Free to start. Your first signed contract takes three minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <BetaLaunchLink variant="cta" />
        </motion.div>

        {/* The circular "wormhole" graphic from CallToAction.tsx, brought
            back per request. The ball's position is driven directly by this
            wrapper's own scroll progress (useScroll, no pinning, no extra
            scroll spacer) — scrolling down moves it forward along the path,
            scrolling up moves it back. */}
        <motion.div
          ref={wormholeRef}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative w-full max-w-5xl mx-auto mt-12 sm:mt-16 px-4 pointer-events-none"
        >
          <div className="relative w-full">
            {/* Rendered as a CSS background, not an <img>, so it doesn't get
                indexed as a decorative image in Google Images results. */}
            <div
              aria-hidden="true"
              role="presentation"
              className="w-full bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: 'url(/circularHole.svg)', aspectRatio: '1535 / 428' }}
            />

            <svg className="w-full h-full absolute top-0 pointer-events-none overflow-visible" viewBox="0 0 508 168">
              <defs>
                <filter id="cta-ball-glow" x="-100%" y="-100%" width="300%" height="300%">
                  <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="white" floodOpacity="0.8" />
                </filter>
              </defs>

              <motion.circle
                cx="0"
                cy="0"
                r="6"
                fill="white"
                filter="url(#cta-ball-glow)"
                style={{
                  offsetPath:
                    'path("M 507.17 20.5339 C 508.493 15.9689 393.859 -0.340403 325.67 0.533854 C 257.481 1.40811 221.56 -1.45657 154.67 10.5339 C 87.781 22.5243 33.1704 43.0339 33.1704 43.0339 L 0.67041 86.5339 L 45.6704 128.034 L 154.67 156.534 L 282.67 166.534 L 397.67 150.034 L 455.67 122.534 L 462.67 97.5339 C 462.67 97.5339 475.769 81.789 451.67 72.0339 C 427.571 62.2787 413.444 59.8959 387.67 58.5339 C 358.38 56.0936 341.961 56.3113 312.67 58.5339 C 282.06 63.6897 269.23 66.662 250.17 72.0339 C 232.226 77.526 226.257 83.2144 222.17 97.5339 L 288.67 128.034 L 294.67 156.534")',
                  offsetDistance: ballOffsetDistance,
                  opacity: ballOpacity,
                }}
              />
            </svg>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
